import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { HandleLoadingState } from '../../decorators/handle-loading-state.decorator';
import { getUrlParams } from '../../functions/get-url-params';
import {
  AlertMessage,
  AlertMessageStatus,
} from '../../models/api/alerts/alert-message';
import { Action } from '../../models/core/action';
import { AlertsDataService } from '../../services/alerts/alerts-data.service';
import { AlertsService } from '../../services/alerts/alerts.service';
import { ExportService } from '../../services/export/export.service';
import { Snackbar } from '../../services/snackbar/snackbar.service';
import { BaseComponent } from '../base/base.component';
import { ExportFormat } from '../core/buttons/export-button.component';
import { ActionButtonComponent } from '../core/buttons/action-button.component';
import { ClaimLabelComponent } from '../core/labels/claim-label.component';
import { TagComponent } from '../core/labels/tag.component';
import { DetailItemComponent } from '../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../core/panels/detail-panel/detail-panel.component';
import { MenuComponent } from '../core/panels/menu.component';
import {
  ColumnComponent,
  ColumnFilterOption,
} from '../core/table/column.component';
import { TableComponent } from '../core/table/table.component';

@Component({
  selector: 'app-alerts-messages',
  imports: [
    TableComponent,
    ColumnComponent,
    TagComponent,
    MenuComponent,
    ClaimLabelComponent,
    DetailPanelComponent,
    DetailItemComponent,
    ActionButtonComponent,
  ],
  template: `
    <div>
      @if (
        alertsDataService.messageFetchCount === 1000 &&
        totalCount > 1000 &&
        !loading
      ) {
        <div class="tw-flex tw-flex-row-reverse tw-py-4">
          <span>
            Showing most recent 1000 messages.
            <a routerLink="" (click)="loadAllMessages()">Click here</a> to load
            all messages.
          </span>
        </div>
      }

      <app-table
        #messageTable
        [dataSource]="messages"
        [globalFilterFields]="['eventNumber']"
        filterPlaceholder="Event number"
        [showSelection]="true"
        selectionKey="messageId"
        [expandable]="true"
        [rowExpandLabel]="rowExpandLabel"
        [showExport]="true"
        [showActions]="true"
        [actionsMenuExpanded]="actionMenuExpanded"
        [showFilterButton]="true"
        sortField="timeDetected"
        [sortOrder]="-1"
        [showRefresh]="true"
        (actionClick)="tableMenuShow($event, actionMenu)"
        recordLabel="alert message"
        dataKey="messageId"
        scrollHeight="800px"
        (selectedEvent)="onSelectedEvent($event)"
        (rowExpand)="onRowExpand($event)"
        (export)="onExport($event)"
        [loading]="loading"
        [loadingHeight]="600"
        dataKey="alertMessageId"
        (refresh)="onRefresh()"
        [error]="error"
      >
        @if (showClientColumn) {
          <app-column name="clientName" label="Client" filterType="select" />
        }

        <app-column name="claimNumber" label="Claim" filterType="text">
          <ng-template let-data>
            <app-claim-label
              [claim]="data"
              [fileNumber]="data.claimNumber"
              [recordSource]="data.recordSource"
              [recordId]="data.recordId"
            />
          </ng-template>
        </app-column>
        <app-column name="claimantName" label="Claimant" filterType="text" />
        <app-column name="alertDescription" label="Alert" filterType="select">
          <ng-template let-data>
            @if (data.alertMessageId === highlightMessageId) {
              <b>{{ data.alertDescription }}</b>
            } @else {
              <span>{{ data.alertDescription }}</span>
            }
          </ng-template>
        </app-column>
        <app-column name="lob" label="LOB" filterType="select" />
        <app-column
          name="messageStatus"
          label="Status"
          filterType="select"
          [customFilterOptions]="statusOptions"
        >
          <ng-template let-data>
            @if (data.messageStatus === 'NEW') {
              <app-tag type="Green">Unread</app-tag>
            } @else {
              <app-tag type="Grey">Read</app-tag>
            }
          </ng-template>
        </app-column>
        <app-column
          name="timeDetected"
          label="Time detected"
          format="dt"
          filterType="date"
        />
        <app-column
          name="actions"
          label="Actions"
          headerClasses="tw-text-center"
          [frozen]="false"
          [sort]="false"
        >
          <ng-template let-data>
            <app-action-button
              [ariaLabel]="rowActionLabel(data)"
              [menu]="rowMenu"
              [row]="data"
              (click)="rowMenu.toggle($event, data)"
            />
          </ng-template>
        </app-column>
        <ng-template #expand let-data>
          <div class="tw-flex tw-gap-8 tw-pb-8 tw-pt-4">
            <app-detail-panel [dataSource]="data" class="tw-basis-1/3">
              <app-detail-item
                label="Condition"
                name="conditionText"
                [wrapValue]="true"
              >
                <ng-template #value let-data>
                  <span class="tw-max-w-[340px] tw-whitespace-pre-line">{{
                    data.conditionText
                  }}</span>
                </ng-template>
              </app-detail-item>

              <app-detail-item
                [label]="data.previousLabel"
                name="previousValue"
              />
              <app-detail-item
                [label]="data.currentLabel"
                name="currentValue"
              />
            </app-detail-panel>

            <app-detail-panel [dataSource]="data" class="tw-basis-1/3">
              <app-detail-item label="Event number" name="eventNumber" />
              @if (data.accountNumber || data.accountName) {
                <app-detail-item label="Account" name="account">
                  <ng-template #value>
                    {{ formatPair(data.accountNumber, data.accountName) }}
                  </ng-template>
                </app-detail-item>
              }
              @if (data.unitNumber || data.unitName) {
                <app-detail-item label="Unit" name="unit">
                  <ng-template #value>
                    {{ formatPair(data.unitNumber, data.unitName) }}
                  </ng-template>
                </app-detail-item>
              }
              <app-detail-item label="Loss state" name="lossState" />
              <app-detail-item label="Loss date" name="lossDate" format="d" />
              @if (data.openDate) {
                <app-detail-item label="Open date" name="openDate" format="d" />
              }
            </app-detail-panel>
          </div>
        </ng-template>
      </app-table>
      @if (!loading) {
        <div class="tw-text-[var(--sed-black-60-color)] tw-text-sm tw-pt-4">
          <i>All alert messages are retained for 30 days.</i>
        </div>
      }

      <app-menu
        [items]="actions"
        #actionMenu
        (click)="onAction($event)"
        (visibilityChange)="actionMenuExpanded = $event"
      />
      <app-menu [items]="rowActions" #rowMenu (click)="onAction($event)" />
    </div>
  `,
  styles: ``,
})
export class AlertsMessagesComponent extends BaseComponent implements OnInit {
  private snackbar = inject(Snackbar);
  private exportService = inject(ExportService);
  private alertsService = inject(AlertsService);
  protected alertsDataService = inject(AlertsDataService);

  @ViewChild('messageTable')
  messageTable: TableComponent;

  showClientColumn = true;

  protected rowExpandLabel(row: AlertMessage): string {
    const alertName = row.alertDescription?.trim();
    if (alertName) {
      return alertName;
    }

    const claim = row.claimNumber?.trim();
    return claim || 'alert message';
  }

  protected rowActionLabel(row: AlertMessage): string {
    const name = row.alertDescription?.trim();
    if (name) {
      return `${name} Actions`;
    }

    const claim = row.claimNumber?.trim();
    return claim ? `${claim} Actions` : 'Actions';
  }

  selectedMessages: any[] = [];

  /** Toolbar Actions menu open state for aria-expanded. */
  actionMenuExpanded = false;

  messages: AlertMessage[] = [];

  loading = false;
  error: any = null;

  totalCount = 0;

  totalNewMessages = 0;

  totalReadMessages = 0;

  highlightMessageId: number | null = null;

  actions: Action[] = [
    { label: 'Mark selected as read', key: 'selected-read' },
    { label: 'Mark all as read', key: 'all-read' },
    { label: 'Mark selected as unread', key: 'selected-unread' },
    { label: 'Mark all as unread', key: 'all-unread' },
    { label: 'Delete selected', key: 'selected-delete' },
    { label: 'Delete all', key: 'all-delete' },
  ];

  rowActions: Action[] = [
    { label: 'Mark as read', key: 'selected-row-read' },
    { label: 'Mark as unread', key: 'selected-row-unread' },
    { label: 'Delete', key: 'selected-row-delete' },
  ];

  statusOptions: ColumnFilterOption[] = [
    {
      count: 0,
      label: 'Read',
      value: 'READ',
    },
    {
      count: 0,
      label: 'Unread',
      value: 'NEW',
    },
  ];

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    await this.loadMessages();
    this.setFiltersFromRoute();
    const listResponse = await this.api.list.get();
    this.showClientColumn = listResponse.clients.length > 1;
    this.dispose.subs = this.alertsDataService.messagesChanged.subscribe(
      async (messages) => {
        this.messages = messages;
        await this.alertsService.enrichPolicyLimitMessageConditions(
          this.messages,
        );
        this.messageTable?.createFilterOptions();
      },
    );

    this.dispose.subs = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (event.url.startsWith('/alerts')) {
          this.setFiltersFromRoute();
        }
      }
    });

    // get the count of messages and if greater than 1000, prompt to load all
    await this.loadMessageCounts();
  }

  @HandleLoadingState()
  async loadAllMessages() {
    // capping at 10k for a fetch all
    await this.alertsDataService.updateMessageFetchCount(10000);
  }

  protected formatPair(left: unknown, right: unknown): string {
    const l = left == null ? '' : String(left).trim();
    const r = right == null ? '' : String(right).trim();

    if (l.length > 0 && r.length > 0) {
      return `${l} - ${r}`;
    }

    return l.length > 0 ? l : r;
  }

  /**
   * Set filters from query params when navigating from notification card
   * Caret icon opens Alert Messages page with message highlight
   */
  setFiltersFromRoute() {
    const params = getUrlParams();
    const messageId = params.messageId;
    this.highlightMessageId = messageId ? parseInt(messageId, 10) : null;

    if (!this.highlightMessageId) {
      return;
    }

    const alertMessage = this.messages.find(
      (x) => x.alertMessageId === this.highlightMessageId,
    );

    const filters: any = alertMessage
      ? {
          claimNumber: [
            {
              value: alertMessage.claimNumber,
              matchMode: 'equals',
              operator: 'and',
            },
          ],
        }
      : {};

    this.messageTable?.applyFilter(filters);
  }

  updateMenu(key: string, badge: number) {
    const item = this.actions.find((x) => x.key === key);
    item.badge = badge;
    item.disabled = false;
  }

  tableMenuShow(event: Event, actionMenu: MenuComponent): void {
    const selected = this.selectedMessages?.length ?? 0;
    this.updateMenu('selected-read', selected);
    this.updateMenu('all-read', this.totalNewMessages);
    this.updateMenu('selected-unread', selected);
    this.updateMenu('all-unread', this.totalReadMessages);
    this.updateMenu('selected-delete', selected);
    this.updateMenu('all-delete', this.totalCount);
    actionMenu.toggle(event);
  }

  protected async loadMessages(refreshCache = false): Promise<void> {
    this.messages = await this.alertsDataService.getMessages(refreshCache);
    await this.alertsService.enrichPolicyLimitMessageConditions(this.messages);
  }

  private async loadMessageCounts(refreshCache = false): Promise<void> {
    const counts = await this.alertsDataService.getMessageCounts(refreshCache);
    this.totalCount = 0;
    this.totalNewMessages = 0;
    this.totalReadMessages = 0;
    for (const count of counts) {
      this.totalCount += count.totalMessages ?? 0;
      this.totalNewMessages += count.newMessages ?? 0;
    }
    this.totalReadMessages = this.totalCount - this.totalNewMessages;
  }

  async onRefresh(): Promise<void> {
    await Promise.all([this.loadMessages(true), this.loadMessageCounts(true)]);
  }

  onSelectedEvent(event: any[]) {
    this.selectedMessages = event;
  }

  /**
   * When a row is expanded, automatically mark the message as read
   * This matches the classic behavior where clicking the plus icon marks as read
   */
  async onRowExpand(event: any) {
    const message = event.data as AlertMessage;
    if (message?.messageStatus === 'NEW') {
      await this.setStatusAndUpdateCounts([message], 'READ');
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onAction(action: Action) {
    switch (action.key) {
      case 'selected-read': {
        if (!this.selectedMessages?.length) {
          this.showError('At least one message needs to be selected');
          return;
        }

        const readCount = this.selectedMessages.filter(
          (m) => m.messageStatus === 'NEW',
        ).length;
        await this.setStatusAndUpdateCounts(this.selectedMessages, 'READ');
        this.showSuccess(
          `${readCount} ${readCount === 1 ? 'message' : 'messages'} marked as read`,
        );
        break;
      }

      case 'all-read':
        await this.alertsDataService.setStatus(this.messages, 'READ');
        await this.loadMessageCounts(true);
        this.showSuccess('All messages marked as read');
        break;
      case 'selected-unread': {
        if (!this.selectedMessages?.length) {
          this.showError('At least one message needs to be selected');
          return;
        }

        const unreadCount = this.selectedMessages.filter(
          (m) => m.messageStatus === 'READ',
        ).length;
        await this.setStatusAndUpdateCounts(this.selectedMessages, 'NEW');
        this.showSuccess(
          `${unreadCount} ${unreadCount === 1 ? 'message' : 'messages'} marked as unread`,
        );
        break;
      }

      case 'all-unread':
        await this.alertsDataService.setStatus(this.messages, 'NEW');
        await this.loadMessageCounts(true);
        this.showSuccess('All messages marked as unread');
        break;
      case 'selected-delete':
        this.deleteMessages(this.selectedMessages, false);
        break;
      case 'all-delete':
        this.deleteMessages(this.messages, true);
        break;
      case 'selected-row-read':
        await this.setStatusAndUpdateCounts([action.data], 'READ');
        this.showSuccess('Message marked as read');
        break;
      case 'selected-row-unread':
        await this.setStatusAndUpdateCounts([action.data], 'NEW');
        this.showSuccess('Message marked as unread');
        break;
      case 'selected-row-delete':
        this.deleteMessages([action.data], false);
        break;
      default:
        break;
    }
  }

  private async setStatusAndUpdateCounts(
    messages: AlertMessage[],
    newStatus: AlertMessageStatus,
  ): Promise<void> {
    const previousStatus = newStatus === 'READ' ? 'NEW' : 'READ';
    const changingCount = messages.filter(
      (m) => m.messageStatus === previousStatus,
    ).length;

    await this.alertsDataService.setStatus(messages, newStatus);

    if (changingCount > 0) {
      if (newStatus === 'READ') {
        this.totalNewMessages = Math.max(
          0,
          this.totalNewMessages - changingCount,
        );
        this.totalReadMessages += changingCount;
      } else {
        this.totalNewMessages += changingCount;
        this.totalReadMessages = Math.max(
          0,
          this.totalReadMessages - changingCount,
        );
      }
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async deleteMessages(
    messages: AlertMessage[],
    isDeleteAll: boolean,
  ): Promise<void> {
    if (messages.length === 0) {
      this.showError('At least one message needs to be selected');
      return;
    }

    const deletingAllRecords =
      isDeleteAll || messages.length === this.messages.length;

    const message = deletingAllRecords
      ? 'All messages will be permanently deleted.'
      : 'All messages shown in the filtered results will be permanently deleted';

    const successMessage = deletingAllRecords
      ? 'All messages deleted'
      : messages.length === 1
        ? 'Message deleted'
        : `${messages.length} messages deleted`;

    this.confirm.show({
      label: 'Delete alert messages',
      message,
      width: '35rem',
      yesLabel: 'Confirm',
      noLabel: 'Close',
      confirm: async () => {
        this.alertsDataService.deleteMessages(messages);
        await this.loadMessageCounts(true);
        this.showSuccess(successMessage);
      },
    });
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onExport(format: ExportFormat): Promise<void> {
    const firstMsgWithLabels = this.messages.find(
      (m) => (m.currentLabel ?? '').trim() || (m.previousLabel ?? '').trim(),
    );

    const currentHeader =
      (firstMsgWithLabels?.currentLabel ?? '').trim() || 'Current Value';
    const previousHeader =
      (firstMsgWithLabels?.previousLabel ?? '').trim() || 'Previous Value';

    const headers: string[] = [
      'Alert Name (formerly Description)',
      'Claimant',
      'Claim Number',
      'Event Number',
      'Date of Loss',
      'Line of Business / LOB',
      'Client #',
      'Client Name',
      'State of Loss',
      'Time Detected',
      'Account Name',
      'Unit Name',
      'Condition',
      currentHeader,
      previousHeader,
    ];

    const rows: any[][] = this.messages.map((msg) => [
      msg.alertDescription ?? '',
      msg.claimantName ?? '',
      msg.claimNumber ?? '',
      msg.eventNumber ?? '',
      this.getExportValue(msg, 'lossDate'),
      msg.lob ?? '',
      msg.clientNumber ?? '',
      this.getClientNameForExport(msg),
      msg.lossState ?? '',
      this.getExportValue(msg, 'timeDetected'),
      msg.accountName ?? '',
      msg.unitName ?? '',
      msg.conditionText ?? '',
      msg.currentValue ?? '',
      msg.previousValue ?? '',
    ]);

    const recordLabel = 'alert-messages';
    const date = new Date().toISOString().split('T')[0];
    const filename = `${recordLabel}-${date}`;
    const sheetName = filename.length > 31 ? recordLabel : filename;

    if (format === 'csv') {
      await this.exportService.exportCsv(
        { fileName: `${filename}.csv`, headers, rows },
        `${filename}.csv`,
      );
    } else if (format === 'xlsx') {
      await this.exportService.exportExcel(
        {
          fileName: `${filename}.xlsx`,
          sheets: [{ label: sheetName, headers, rows }],
        },
        `${filename}.xlsx`,
      );
    }
    this.showSuccess('Export completed successfully.');
  }

  private getClientNameForExport(msg: AlertMessage): string {
    const raw = (msg.clientName ?? '').trim();
    if (!raw) {
      return '';
    }

    if (msg.clientNumber == null) {
      return raw;
    }

    const clientNum = String(msg.clientNumber).trim();
    if (!clientNum) {
      return raw;
    }

    const prefix = `${clientNum} - `;
    if (raw.startsWith(prefix)) {
      return raw.substring(prefix.length).trim();
    }

    return raw;
  }

  private getExportValue(msg: AlertMessage, propertyName: string): string {
    const value = (msg as any)[propertyName];

    if (value === null || value === undefined) {
      return '';
    }

    const lowerProp = propertyName.toLowerCase();
    const looksLikeDate =
      value instanceof Date ||
      lowerProp.includes('date') ||
      lowerProp.includes('time') ||
      lowerProp.includes('detected');

    if (looksLikeDate) {
      const dateValue = value instanceof Date ? value : new Date(value);
      if (!isNaN(dateValue.getTime())) {
        const includeTime =
          lowerProp.includes('time') || lowerProp.includes('detected');
        if (includeTime) {
          return dateValue.toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
        }

        return dateValue.toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
        });
      }
    }

    return String(value);
  }

  private showSuccess(message: string): void {
    this.snackbar.show(message, 'Success', 'fa-light fa-check', 4000);
  }

  private showError(message: string): void {
    this.snackbar.show(message, 'Error', 'fa-light fa-xmark', 4000);
  }
}
